import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import hrService from '../../services/hrService';
import { toast } from 'react-toastify';
import { 
  MdMenu, 
  MdNotifications, 
  MdViewSidebar,
  MdKeyboardArrowDown,
  MdFileUpload,
  MdCheck,
  MdWarning
} from 'react-icons/md';

/**
 * GlobalHeader Component
 * Contains notifications and user menu
 */
const GlobalHeader = ({ onToggleLeftSidebar, onToggleRightSidebar, showRightSidebarToggle }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showResignModal, setShowResignModal] = useState(false);
  const [resignationReason, setResignationReason] = useState('');
  const [resignationFile, setResignationFile] = useState(null);
  const [submittingResignation, setSubmittingResignation] = useState(false);
  const [resignationRequest, setResignationRequest] = useState(null);
  const [resignationModalMode, setResignationModalMode] = useState('initial'); // 'initial' or 'approved'

  // Check resignation status when modal opens
  useEffect(() => {
    if (showResignModal && (user?.role === 'HR' || user?.role === 'HRO')) {
      checkResignationStatus();
    }
  }, [showResignModal]);

  const checkResignationStatus = async () => {
    try {
      const response = await hrService.getResignationRequest();
      if (response.data) {
        setResignationRequest(response.data);
        if (response.data.status === 'APPROVED') {
          setResignationModalMode('approved');
        } else {
          setResignationModalMode('initial');
        }
      }
    } catch (error) {
      // No existing resignation request
      setResignationRequest(null);
      setResignationModalMode('initial');
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
    setShowUserMenu(false);
  };

  const confirmLogout = () => {
    logout();
    setShowLogoutModal(false);
    navigate('/login');
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  const handleResignClick = () => {
    setShowResignModal(true);
    setShowUserMenu(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!['application/pdf', 'image/jpeg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(file.type)) {
        toast.error('Please upload a valid document (PDF, Word, or Image)');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must not exceed 5MB');
        return;
      }
      setResignationFile(file);
    }
  };

  const handleResignationSubmit = async () => {
    if (!resignationReason.trim()) {
      toast.error('Please provide a reason for resignation');
      return;
    }

    if (resignationReason.trim().length < 10) {
      toast.error('Resignation reason must be at least 10 characters long');
      return;
    }

    if (!resignationFile) {
      toast.error('Please upload your resignation letter');
      return;
    }

    setSubmittingResignation(true);
    try {
      // Submit resignation with file to backend
      const response = await hrService.uploadResignationLetter(resignationFile, resignationReason);
      
      if (response.success || response.data) {
        toast.success('Resignation letter submitted successfully. Awaiting admin approval.');
        setShowResignModal(false);
        setResignationReason('');
        setResignationFile(null);
        setResignationRequest(response.data);
      } else {
        toast.error(response.error || 'Failed to submit resignation');
      }
    } catch (error) {
      console.error('Error submitting resignation:', error);
      toast.error(error.response?.data?.error || 'Failed to submit resignation');
    } finally {
      setSubmittingResignation(false);
    }
  };

  const handleFinalizeResignation = async () => {
    if (!resignationRequest || resignationRequest.status !== 'APPROVED') {
      toast.error('Your resignation has not been approved yet');
      return;
    }

    setSubmittingResignation(true);
    try {
      const response = await hrService.finalizeResignation();
      
      if (response.success || response.data) {
        toast.success('Resignation finalized. Your account has been deactivated.');
        setShowResignModal(false);
        
        // Logout after successful resignation
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 2000);
      } else {
        toast.error(response.error || 'Failed to finalize resignation');
      }
    } catch (error) {
      console.error('Error finalizing resignation:', error);
      toast.error(error.response?.data?.error || 'Failed to finalize resignation');
    } finally {
      setSubmittingResignation(false);
    }
  };

  const cancelResignation = () => {
    setShowResignModal(false);
    setResignationReason('');
    setResignationFile(null);
  };

  const getDisplayName = () => {
    const joinedName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
    return (
      user?.name ||
      user?.fullName ||
      (joinedName || null) ||
      user?.username ||
      (user?.email ? user.email.split('@')[0] : null) ||
      user?.employeeId ||
      user?.phoneNumber ||
      'User'
    );
  };

  const getDisplayRole = () => {
    const roleMap = {
      ADMIN: 'Admin',
      HR: 'HR',
      HRO: 'HRO',
      CANDIDATE: 'Candidate',
      CHAIRMAN: 'Chairman'
    };
    return roleMap[user?.role] || user?.role || 'User';
  };

  const displayName = getDisplayName();
  const displayRole = getDisplayRole();

  return (
    <header className="sticky top-0 z-50 flex h-[60px] items-center justify-between border-b border-border bg-white px-4 sm:px-6">
      <div className="flex max-w-[600px] flex-1 items-center gap-2 sm:gap-4">
        <button 
          className="rounded-md bg-transparent p-2 text-2xl text-charcoal transition-colors hover:bg-background"
          onClick={onToggleLeftSidebar}
          aria-label="Toggle sidebar"
        >
          <MdMenu size={24} />
        </button>
      </div>

      <div className="flex items-center gap-1 sm:gap-3">
        {showRightSidebarToggle && (
          <button 
            className="relative bg-transparent border-none text-xl cursor-pointer p-2 rounded-md transition-colors hover:bg-background"
            onClick={onToggleRightSidebar}
            aria-label="Toggle right sidebar"
          >
            <MdViewSidebar size={22} />
          </button>
        )}
      </div>

      <div className="flex items-center gap-1 sm:gap-3">
        {showRightSidebarToggle && (
          <button 
            className="relative bg-transparent border-none text-xl cursor-pointer p-2 rounded-md transition-colors hover:bg-background"
            onClick={onToggleRightSidebar}
            aria-label="Toggle right sidebar"
          >
            <MdViewSidebar size={22} />
          </button>
        )}

        <button className="relative bg-transparent border-none text-xl cursor-pointer p-2 rounded-md transition-colors hover:bg-background" aria-label="Notifications">
          <MdNotifications className="block" size={22} />
          <span className="absolute top-1 right-1 bg-terracotta text-white text-[10px] font-semibold px-[5px] py-[2px] rounded-[10px] min-w-[16px] text-center">3</span>
        </button>

        <div className="relative">
          <button 
            className="flex items-center gap-2 bg-transparent border-none cursor-pointer py-[6px] px-3 rounded-md transition-colors hover:bg-background"
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            {user?.profilePhoto?.url || user?.photoUrl ? (
              <img 
                src={user.profilePhoto?.url || user.photoUrl} 
                alt={displayName}
                className="w-8 h-8 rounded-full object-cover border-2 border-olive"
                onError={(e) => {
                  // Fallback to initial avatar if image fails to load
                  e.target.style.display = 'none';
                  const fallback = e.target.nextElementSibling;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
            ) : null}
            <span 
              className="w-8 h-8 rounded-full bg-olive text-white flex items-center justify-center font-semibold text-sm"
              style={{display: user?.profilePhoto?.url || user?.photoUrl ? 'none' : 'flex'}}
            >
              {displayName?.charAt(0)?.toUpperCase() || 'U'}
            </span>
            <span className="hidden text-sm font-medium text-charcoal lg:block">{displayName}</span>
            <MdKeyboardArrowDown className="hidden text-[10px] text-[#999] lg:block" size={18} />
          </button>

          {showUserMenu && (
            <div className="absolute top-full right-0 mt-2 bg-white border border-border rounded-md shadow-md min-w-[200px] py-2 z-[100]">
              <div className="py-3 px-4">
                <div className="text-sm font-semibold text-charcoal">Name: {displayName}</div>
                <div className="text-sm font-semibold text-charcoal">Role: {displayRole}</div>
              </div>
              <div className="h-px bg-border my-2"></div>
              <button 
                className="w-full text-left bg-transparent border-none py-[10px] px-4 text-sm text-charcoal cursor-pointer transition-colors hover:bg-background"
                onClick={() => {
                  const roleRoutes = {
                    'CANDIDATE': '/candidate/profile',
                    'HR': '/hr/profile',
                    'HRO': '/hro/profile',
                    'ADMIN': '/admin/profile',
                    'CHAIRMAN': '/chairman/profile'
                  };
                  navigate(roleRoutes[user?.role] || '/profile');
                  setShowUserMenu(false);
                }}
              >
                Profile
              </button>
              <button 
                className="w-full text-left bg-transparent border-none py-[10px] px-4 text-sm text-charcoal cursor-pointer transition-colors hover:bg-background"
                onClick={() => navigate('/settings')}
              >
                Settings
              </button>
              {(user?.role === 'HR' || user?.role === 'HRO') && (
                <button 
                  className="w-full text-left bg-transparent border-none py-[10px] px-4 text-sm text-orange-600 cursor-pointer transition-colors hover:bg-background"
                  onClick={handleResignClick}
                >
                  Resignation
                </button>
              )}
              <div className="h-px bg-border my-2"></div>
              <button 
                className="w-full text-left bg-transparent border-none py-[10px] px-4 text-sm text-terracotta cursor-pointer transition-colors hover:bg-background"
                onClick={handleLogoutClick}
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {showLogoutModal && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center z-[1000]" onClick={cancelLogout}>
          <div className="bg-white rounded-xl p-6 max-w-[400px] w-[90%] shadow-lg" onClick={(e) => e.stopPropagation()}>
            <h3 className="m-0 mb-3 text-xl font-semibold text-primary">Confirm Logout</h3>
            <p className="m-0 mb-6 text-text-secondary text-sm">Are you sure you want to logout?</p>
            <div className="flex gap-3 justify-end">
              <button className="py-[10px] px-5 rounded-md font-semibold text-sm cursor-pointer transition-all border-none bg-[#F5F5F5] text-text-primary hover:bg-[#E0E0E0]" onClick={cancelLogout}>
                Cancel
              </button>
              <button className="py-[10px] px-5 rounded-md font-semibold text-sm cursor-pointer transition-all border-none bg-status-error text-white hover:bg-[#B71C1C]" onClick={confirmLogout}>
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {showResignModal && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/50 flex items-center justify-center z-[1000]" onClick={cancelResignation}>
          <div className="bg-white rounded-xl p-6 max-w-[500px] w-[90%] shadow-lg" onClick={(e) => e.stopPropagation()}>
            {resignationModalMode === 'initial' && (
              <>
                <h3 className="m-0 mb-3 text-xl font-semibold text-orange-600 flex items-center gap-2">
                  <MdWarning size={20} />
                  Resolution Submission
                </h3>
                <p className="m-0 mb-4 text-text-secondary text-sm">
                  Submit your resignation letter for admin approval. You will need to confirm after approval.
                </p>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-charcoal mb-2">
                    Resignation Letter (PDF/Word/Image) *
                  </label>
                  <div className="border-2 border-dashed border-border rounded-md p-4 text-center cursor-pointer hover:border-primary hover:bg-blue-50 transition-all">
                    <input
                      type="file"
                      accept="application/pdf,.doc,.docx,image/jpeg,image/png"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="resignation-file-input"
                      disabled={submittingResignation}
                    />
                    <label htmlFor="resignation-file-input" className="cursor-pointer flex flex-col items-center gap-2">
                      <MdFileUpload size={32} className="text-primary" />
                      <span className="text-sm font-medium text-charcoal">
                        {resignationFile ? resignationFile.name : 'Click to upload or drag file'}
                      </span>
                      <span className="text-xs text-text-secondary">PDF, Word, or Image (Max 5MB)</span>
                    </label>
                  </div>
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-charcoal mb-2">
                    Reason for Resignation *
                  </label>
                  <textarea
                    value={resignationReason}
                    onChange={(e) => setResignationReason(e.target.value)}
                    placeholder="Please explain your reason for resignation (minimum 10 characters)..."
                    className="w-full p-3 border border-border rounded-md resize-none h-24 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    disabled={submittingResignation}
                  />
                  <span className="text-xs text-text-secondary mt-1">
                    {resignationReason.length}/1000 characters
                  </span>
                </div>

                <div className="flex gap-3 justify-end">
                  <button 
                    className="py-[10px] px-5 rounded-md font-semibold text-sm cursor-pointer transition-all border-none bg-[#F5F5F5] text-text-primary hover:bg-[#E0E0E0] disabled:opacity-50 disabled:cursor-not-allowed" 
                    onClick={cancelResignation}
                    disabled={submittingResignation}
                  >
                    Cancel
                  </button>
                  <button 
                    className="py-[10px] px-5 rounded-md font-semibold text-sm cursor-pointer transition-all border-none bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed" 
                    onClick={handleResignationSubmit}
                    disabled={submittingResignation || !resignationReason.trim() || !resignationFile || resignationReason.length < 10}
                  >
                    {submittingResignation ? 'Submitting...' : 'Submit for Approval'}
                  </button>
                </div>
              </>
            )}

            {resignationModalMode === 'approved' && resignationRequest && (
              <>
                <h3 className="m-0 mb-3 text-xl font-semibold text-green-600 flex items-center gap-2">
                  <MdCheck size={20} />
                  Resignation Approved
                </h3>
                <p className="m-0 mb-4 text-text-secondary text-sm">
                  Your resignation has been approved by the admin. Click "Confirm Resignation" to finalize the process.
                </p>
                
                <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
                  <div className="text-sm">
                    <p className="mb-2"><strong>Status:</strong> <span className="text-green-600">APPROVED</span></p>
                    <p className="mb-2"><strong>Approved By:</strong> {resignationRequest.approvedBy?.phoneNumber || 'Admin'}</p>
                    <p className="mb-0"><strong>Approved At:</strong> {new Date(resignationRequest.approvedAt).toLocaleDateString()}</p>
                  </div>
                </div>

                <p className="m-0 mb-6 text-text-secondary text-sm">
                  Upon confirmation, your account will be deactivated and you will be logged out.
                </p>

                <div className="flex gap-3 justify-end">
                  <button 
                    className="py-[10px] px-5 rounded-md font-semibold text-sm cursor-pointer transition-all border-none bg-[#F5F5F5] text-text-primary hover:bg-[#E0E0E0] disabled:opacity-50 disabled:cursor-not-allowed" 
                    onClick={cancelResignation}
                    disabled={submittingResignation}
                  >
                    Close
                  </button>
                  <button 
                    className="py-[10px] px-5 rounded-md font-semibold text-sm cursor-pointer transition-all border-none bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed" 
                    onClick={handleFinalizeResignation}
                    disabled={submittingResignation}
                  >
                    {submittingResignation ? 'Confirming...' : 'Confirm Resignation'}
                  </button>
                </div>
              </>
            )}

            {resignationModalMode === 'initial' && resignationRequest && resignationRequest.status === 'PENDING' && (
              <>
                <h3 className="m-0 mb-3 text-xl font-semibold text-yellow-600 flex items-center gap-2">
                  <MdWarning size={20} />
                  Pending Approval
                </h3>
                <p className="m-0 mb-4 text-text-secondary text-sm">
                  Your resignation is awaiting admin approval. You will be notified once it's approved.
                </p>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
                  <div className="text-sm">
                    <p className="mb-0"><strong>Status:</strong> <span className="text-yellow-600">PENDING APPROVAL</span></p>
                  </div>
                </div>

                <div className="flex gap-3 justify-end">
                  <button 
                    className="py-[10px] px-5 rounded-md font-semibold text-sm cursor-pointer transition-all border-none bg-[#F5F5F5] text-text-primary hover:bg-[#E0E0E0]" 
                    onClick={cancelResignation}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default GlobalHeader;
